import LegalPageLayout from "@/components/LegalPageLayout";
import {
  COMPANY_ADDRESS_FULL,
  COMPANY_BRAND_NAME,
  COMPANY_CIF,
  COMPANY_CONTACT_EMAIL,
  COMPANY_LEGAL_NAME,
  COMPANY_NOTIFICATIONS_EMAIL,
  COMPANY_NOTIFY_EMAIL,
  DECLARATION_DATE,
  DECLARATION_PLACE,
  SOFTWARE_CODE,
  SOFTWARE_DECLARATION_URL,
  SOFTWARE_NAME,
  SOFTWARE_PRODUCT_URL,
  SOFTWARE_VERSION,
} from "@/lib/company";

const DeclaracionResponsable = () => {
  return (
    <LegalPageLayout title="Declaración Responsable del Sistema Informático de Facturación">
      <p className="text-gray-600 mb-8 not-prose">
        Declaración responsable del sistema informático de facturación {SOFTWARE_NAME}, conforme al
        artículo 29.2.j) de la Ley 58/2003, General Tributaria, el Real Decreto 1007/2023, de 5 de
        diciembre, y la Orden HAC/1177/2024, de 17 de octubre.
      </p>

      <div className="not-prose mb-10 rounded-xl border border-purple-100 bg-purple-50/60 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Resumen de identificación</h2>
        <dl className="grid gap-3 text-sm text-gray-700 sm:grid-cols-2">
          <div>
            <dt className="font-medium text-gray-900">Sistema informático</dt>
            <dd>{SOFTWARE_NAME}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-900">Código identificador</dt>
            <dd>{SOFTWARE_CODE}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-900">Versión</dt>
            <dd>{SOFTWARE_VERSION}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-900">Modalidad</dt>
            <dd>Exclusivamente VERI*FACTU</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-900">Entidad productora</dt>
            <dd>{COMPANY_LEGAL_NAME}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-900">CIF</dt>
            <dd>{COMPANY_CIF}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="font-medium text-gray-900">Domicilio social y fiscal</dt>
            <dd>{COMPANY_ADDRESS_FULL}, España</dd>
          </div>
        </dl>
      </div>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
          DECLARACIÓN RESPONSABLE DEL SISTEMA INFORMÁTICO DE FACTURACIÓN
        </h2>

        <div className="space-y-6 text-gray-700">
          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.a) Nombre del sistema informático a que se refiere esta declaración responsable:
            </p>
            <p>{SOFTWARE_NAME}</p>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.b) Código identificador del sistema informático a que se refiere el apartado a) de esta
              declaración responsable:
            </p>
            <p>{SOFTWARE_CODE}</p>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.c) Identificador completo de la versión concreta del sistema informático a que se refiere esta
              declaración responsable:
            </p>
            <p>{SOFTWARE_VERSION}</p>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.d) Componentes, hardware y software, de que consta el sistema informático a que se refiere esta
              declaración responsable, junto con una breve descripción de lo que hace dicho sistema informático y
              de sus principales funcionalidades:
            </p>
            <p className="mb-4">
              Se trata de una plataforma informática en la nube (SaaS) comercializada bajo la marca{" "}
              {COMPANY_BRAND_NAME}, accesible a través de navegador web, diseñada para la gestión fiscal y
              contable de trabajadores autónomos, que incluye la expedición, consulta y gestión de facturas y de
              los registros de facturación asociados.
            </p>
            <p className="mb-4">
              El sistema se ejecuta en infraestructura cloud segura, sin necesidad de instalación local por parte
              del usuario, pudiendo accederse desde cualquier dispositivo con conexión a internet y navegador
              compatible.
            </p>
            <p className="mb-4">Entre sus principales funcionalidades se incluyen:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Captura y clasificación automática de información de facturación y gastos.</li>
              <li>Expedición, consulta y gestión de facturas.</li>
              <li>Generación, conservación y remisión de registros de facturación en modalidad VERI*FACTU.</li>
              <li>Gestión de modelos tributarios y obligaciones fiscales de autónomos.</li>
              <li>Informes, estadísticas y exportación de datos de facturación.</li>
              <li>Integración con plataformas de venta online y herramientas de gestión.</li>
              <li>Registro de interesados y solicitudes de acceso anticipado a través del formulario web.</li>
            </ul>
            <p>
              El sistema permite gestionar de forma independiente la facturación de distintos obligados
              tributarios dentro de la plataforma, cumpliendo separadamente con la normativa aplicable a cada
              una de ellas, como si, en la práctica, se tratara de sistemas informáticos de facturación
              distintos.
            </p>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.e) Indicación de si el sistema informático a que se refiere esta declaración responsable se ha
              producido de tal manera que, a los efectos de cumplir con el Reglamento, solo pueda funcionar
              exclusivamente como «VERI*FACTU»:
            </p>
            <p>S – Sí</p>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.f) Indicación de si el sistema informático a que se refiere la declaración responsable permite
              ser usado por varios obligados tributarios o por un mismo usuario para dar soporte a la
              facturación de varios obligados tributarios:
            </p>
            <p>S – Sí</p>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.g) Tipos de firma utilizados para firmar los registros de facturación y de evento en el caso de
              que el sistema informático a que se refiere esta declaración responsable no sea utilizado como
              «VERI*FACTU»:
            </p>
            <p>
              Dado que se trata de un producto de facturación que solo puede ser utilizado exclusivamente en la
              modalidad de «VERI*FACTU», no se realiza una firma electrónica expresa de los registros de
              facturación generados, ya que la normativa considera que quedan firmados al ser remitidos
              correctamente a los servicios electrónicos de la Agencia Tributaria con la debida autenticación
              mediante el adecuado certificado electrónico cualificado.
            </p>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.h) Razón social de la entidad productora del sistema informático a que se refiere esta
              declaración responsable:
            </p>
            <p>
              {COMPANY_LEGAL_NAME} (marca comercial: {COMPANY_BRAND_NAME})
            </p>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.i) Número de identificación fiscal (NIF) español de la entidad productora del sistema
              informático a que se refiere esta declaración responsable:
            </p>
            <p>{COMPANY_CIF}</p>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.j) Dirección postal completa de contacto de la entidad productora del sistema informático a que
              se refiere esta declaración responsable:
            </p>
            <p>
              {COMPANY_ADDRESS_FULL}
              <br />
              España.
            </p>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.k) La entidad productora del sistema informático a que se refiere esta declaración responsable
              hace constar que dicho sistema informático, en la versión indicada en ella, cumple con lo
              dispuesto en el artículo 29.2.j) de la Ley 58/2003, de 17 de diciembre, General Tributaria, en el
              Reglamento que establece los requisitos que deben adoptar los sistemas y programas informáticos o
              electrónicos que soporten los procesos de facturación de empresarios y profesionales, y la
              estandarización de formatos de los registros de facturación, aprobado por el Real Decreto
              1007/2023, de 5 de diciembre, en la Orden HAC/1177/2024, de 17 de octubre, y en la sede
              electrónica de la Agencia Estatal de Administración Tributaria para todo aquello que complete las
              especificaciones de dicha orden.
            </p>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              1.l) Fecha y lugar en que la entidad productora de este sistema informático suscribe esta
              declaración responsable del mismo:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Fecha: {DECLARATION_DATE}.</li>
              <li>Lugar: {DECLARATION_PLACE}.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">ANEXO</h2>

        <div className="space-y-6 text-gray-700">
          <div>
            <p className="font-semibold text-gray-900 mb-2">
              2.a) Otras formas de contacto con la entidad productora del sistema informático a que se refiere
              esta declaración responsable:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Correo electrónico general:{" "}
                <a href={`mailto:${COMPANY_CONTACT_EMAIL}`} className="text-taxby-purple hover:underline">
                  {COMPANY_CONTACT_EMAIL}
                </a>
              </li>
              <li>
                Correo electrónico de notificaciones del sistema:{" "}
                <a
                  href={`mailto:${COMPANY_NOTIFICATIONS_EMAIL}`}
                  className="text-taxby-purple hover:underline"
                >
                  {COMPANY_NOTIFICATIONS_EMAIL}
                </a>
              </li>
              <li>
                Correo electrónico de contacto comercial:{" "}
                <a href={`mailto:${COMPANY_NOTIFY_EMAIL}`} className="text-taxby-purple hover:underline">
                  {COMPANY_NOTIFY_EMAIL}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              2.b) Direcciones de internet de la entidad productora del sistema informático a que se refiere
              esta declaración responsable:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Sitio web de la empresa:{" "}
                <a
                  href={SOFTWARE_PRODUCT_URL}
                  className="text-taxby-purple hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {SOFTWARE_PRODUCT_URL}
                </a>
              </li>
              <li>
                Información sobre este producto en el sitio web de la empresa:{" "}
                <a
                  href={`${SOFTWARE_PRODUCT_URL}/#features`}
                  className="text-taxby-purple hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {SOFTWARE_PRODUCT_URL}/#features
                </a>
              </li>
              <li>
                Acceso al histórico de declaraciones responsables de las versiones de este producto:{" "}
                <a href={SOFTWARE_DECLARATION_URL} className="text-taxby-purple hover:underline">
                  {SOFTWARE_DECLARATION_URL}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              2.c) El sistema informático a que se refiere esta declaración responsable cumple las diferentes
              especificaciones técnicas y funcionales contenidas en la Orden HAC/1177/2024, de 17 de octubre, y
              en la sede electrónica de la Agencia Estatal de Administración Tributaria para todo aquello que
              complete las especificaciones de dicha orden, de la siguiente manera:
            </p>
            <p className="mb-4">
              Además del modo que es de obligado cumplimiento en ciertos casos (como el algoritmo de huella a
              emplear), otras implementaciones utilizadas son:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Arquitectura cloud con procesos transaccionales que garantizan la consolidación, en una sola
                unidad lógica de negocio, de la expedición de la factura y la generación del registro de
                facturación correspondiente.
              </li>
              <li>
                Remisión de registros de facturación a los servicios electrónicos de la Agencia Estatal de
                Administración Tributaria en modalidad «VERI*FACTU», con autenticación mediante certificado
                electrónico cualificado del obligado tributario.
              </li>
              <li>
                Conservación segura, trazabilidad e integridad de los registros de facturación y de evento,
                conforme a los requisitos del Reglamento.
              </li>
              <li>
                Posibilidad de acceso restringido a la información con trascendencia tributaria, de forma que la
                Administración tributaria pueda acceder directamente a la consulta y al resto de funcionalidades
                exigidas sobre la información de los registros de facturación y de eventos, mediante un modo de
                acceso específico habilitado al efecto.
              </li>
              <li>
                Separación lógica de las facturaciones de distintos obligados tributarios dentro de la
                plataforma {COMPANY_BRAND_NAME}, asegurando el cumplimiento independiente de la normativa
                aplicable en cada caso.
              </li>
              <li>
                Canal de captación de solicitudes de acceso anticipado mediante formulario web seguro, sin
                alteración de los registros de facturación ni de los mecanismos de cumplimiento normativo del
                sistema.
              </li>
            </ul>
          </div>
        </div>
      </section>
    </LegalPageLayout>
  );
};

export default DeclaracionResponsable;
